import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../component/Pagination";
import { toast } from "react-toastify";

import SingleColorPicker from "./SingleColorPicker";

import {
  fetchColors,
  createColor,
  updateColor,
  deleteColor,
  toggleColorStatus,
} from "../../../slice/color/colorSlice";

export default function Color() {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 10;

  const { colors = [], loading, pagination = {} } = useSelector(
    (state) => state.color
  );

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedColor, setSelectedColor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  /* FETCH */
  useEffect(() => {
    dispatch(fetchColors({ page, limit }));
  }, [dispatch, page]);

  /* CREATE */
  const handleCreate = (colorData) => {
    // colorData = { name: "Sky Blue", code: "#87CEEB" }
    dispatch(createColor(colorData))
      .unwrap()
      .then(() => {
        toast.success("Color added successfully");
        setShowModal(false);
        dispatch(fetchColors({ page, limit }));
      })
      .catch((err) => toast.error(err));
  };

  /* UPDATE */
  const handleUpdate = (colorData) => {
    dispatch(updateColor({ id: selectedColor._id, ...colorData }))
      .unwrap()
      .then(() => {
        toast.success("Color updated successfully");
        setShowEditModal(false);
        setSelectedColor(null);
        dispatch(fetchColors({ page, limit }));
      })
      .catch((err) => toast.error(err));
  };

  /* DELETE */
  const confirmDelete = () => {
    dispatch(deleteColor(deleteId))
      .unwrap()
      .then(() => {
        toast.success("Color deleted successfully");
        setShowDeleteModal(false);
        setDeleteId(null);
        dispatch(fetchColors({ page, limit }));
      });
  };

  return (
    <div className="row">
      <div className="card mt-3">

        {/* HEADER */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Color List</h4>
          <button className="btn cusBTNPop btn-sm" onClick={() => setShowModal(true)}>
            + Add Color
          </button>
        </div>

        {/* TABLE */}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>SI</th>
                <th>Color Name</th>
                <th>Hex Code</th>   {/* ✅ NEW column */}
                <th>Preview</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">Loading...</td>
                </tr>
              ) : colors.length ? (
                colors.map((color, index) => (
                  <tr key={color._id}>
                    <td>{(page - 1) * limit + index + 1}</td>

                    {/* ✅ Color Name — manually entered */}
                    <td className="fw-semibold">{color.name}</td>

                    {/* ✅ Hex Code */}
                    <td>
                      <code style={{ fontSize: 13 }}>{color.code}</code>
                    </td>

                    {/* ✅ Color Swatch Preview */}
                    <td>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 6,
                          background: color.code,
                          border: "1px solid #dee2e6",
                        }}
                        title={color.code}
                      />
                    </td>

                    {/* ✅ Status Toggle */}
                    <td>
                      <div
                        className={`status-toggle ${color.status ? "enabled" : "disabled"}`}
                        onClick={() => dispatch(toggleColorStatus(color._id))}
                      >
                        <span className="toggle-knob" />
                        <span className="toggle-text">
                          {color.status ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </td>

                    {/* ✅ Actions */}
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={() => {
                            setSelectedColor(color);
                            setShowEditModal(true);
                          }}
                        >
                          <iconify-icon icon="solar:pen-2-broken" />
                        </button>

                        <button
                          className="btn btn-soft-danger btn-sm"
                          onClick={() => {
                            setDeleteId(color._id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-muted">
                    No colors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="card-footer border-top">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>

        {/* ADD MODAL */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Add Color</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <SingleColorPicker
                  onSubmit={handleCreate}
                  onCancel={() => setShowModal(false)}
                />
              </div>
            </div>
          </div>
        )}

        {/* EDIT MODAL */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Edit Color</h5>
                <button className="btn-close" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body">
                <SingleColorPicker
                  initialValue={selectedColor}
                  onSubmit={handleUpdate}
                  onCancel={() => setShowEditModal(false)}
                />
              </div>
            </div>
          </div>
        )}

        {/* DELETE MODAL */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Delete Color</h5>
              </div>
              <div className="modal-body text-center">
                Are you sure you want to delete this color?
              </div>
              <div className="modal-footer">
                <button className="btn cusClose" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button className="btn cuscancel" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}